import { toTotalNavPoints, type UnitsOutstanding, type NavValue } from 'models/priceChart'
import type { Project } from 'models/project'
import { ProjectTypes } from 'constants/enums'
import { toChartPoints, type ChartPoint } from 'models/chart'
import { latestDate } from './shared'
import type { ChartDenomination, ProductChart } from './types'

/** Gold-denominated NAV, published by gold funds next to the fiat one. */
export const GOLD_CURRENCY = 'XAUt'

/**
 * The published monthly alpha compounded into an index, 100 at the first NAV: each month's alpha is
 * its return over the month before. None unless every month has one (then the NAV measures returns).
 */
export const toAlphaIndex = (values: NavValue[]): ChartPoint[] | undefined => {
  const months = toChartPoints(
    values,
    (value) => value.time,
    (value) => value.alpha,
  )
  if (months.length === 0 || months.length !== values.length) return undefined
  let index = 100
  return months.map(([time, alpha], position) => {
    if (position > 0) index *= 1 + alpha / 100
    return [time, index] as ChartPoint
  })
}

/** The newest XAUt NAV (`scudo`), the token price as the fund publishes it in gold. */
export const latestGoldNav = (values: NavValue[] | undefined): number | undefined => {
  let latest: { time: number; value: number } | undefined
  for (const item of values ?? []) {
    const time = new Date(item.time).getTime()
    const value = Number(item.scudo)
    if (!Number.isFinite(time) || !Number.isFinite(value) || value <= 0) continue
    if (!latest || time > latest.time) latest = { time, value }
  }
  return latest?.value
}

/**
 * Fund: monthly unit NAV. A fund that also publishes a gold NAV (Goldstream) opens in gold:
 * that is its reporting unit, and in fiat its return is mostly the gold price. With its units
 * outstanding, the fund's total NAV too (its NAV view and headline), in each of those currencies.
 */
export const toFundNavChart = (
  project: Pick<Project, 'type' | 'tokenSymbol' | 'tokenCurrency'> | undefined,
  nav: NavValue[] | undefined,
  units?: UnitsOutstanding,
): ProductChart | undefined => {
  if (project?.type !== ProjectTypes.FUND) return undefined
  const fiat: ChartDenomination = {
    currency: project.tokenCurrency?.trim() || 'USD',
    points: toChartPoints(
      nav ?? [],
      (value) => value.time,
      (value) => value.price,
    ),
  }
  const goldValues = (nav ?? []).filter((value) => Number(value.scudo) > 0)
  const gold: ChartDenomination = {
    currency: GOLD_CURRENCY,
    points: toChartPoints(
      goldValues,
      (value) => value.time,
      (value) => value.scudo,
    ),
    returnIndex: toAlphaIndex(goldValues),
  }
  const denominations = [gold, fiat].filter((denomination) => denomination.points.length > 0)
  return {
    kind: 'fund',
    symbol: project.tokenSymbol?.trim() || '',
    labelKey: 'stoPage.heroChart.unitNav',
    denominations: denominations.length ? denominations : [fiat],
    fundNavs: units
      ? denominations
          .map(({ currency, points }) => ({ currency, points: toTotalNavPoints(points, units) }))
          .filter((fundNav) => fundNav.points.length > 0)
      : undefined,
    // The default (first) denomination: gold dates can differ from the fiat ones.
    asOf: latestDate((denominations[0] ?? fiat).points),
  }
}
